import { HttpResponse } from '@capacitor/core';
import {
  RESPONSE_STATUS_SERVER_UNAVAILABLE,
  RESPONSE_STATUS_SERVER_TIMEOUT,
  RESPONSE_STATUS_FORCE,
  RESPONSE_STATUS_CONTENT_TOO_LARGE,
} from '../../../constants';
import {
  ServerError,
  ServerTimeout,
  ForceError,
  EntryError,
  ContentTooLargeError,
} from '../../errors';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('forceErrors');

export const processFetcherResponse = (
  response: HttpResponse,
  // Accumulated force value of the request that produced this response —
  // carried into the thrown error so the next answer can be chained onto it.
  forceValue?: string
) => {
  switch (response.status) {
    case RESPONSE_STATUS_SERVER_UNAVAILABLE:
      throw new ServerError(response);
    case RESPONSE_STATUS_SERVER_TIMEOUT:
      throw new ServerTimeout(response);
    // Handled by status alone: a proxy 413 has no JSON error body to read.
    case RESPONSE_STATUS_CONTENT_TOO_LARGE:
      throw new ContentTooLargeError();
    case RESPONSE_STATUS_FORCE:
      // An entry error (|E modifier) can arrive under the force status too.
      if (response.data.error?.entry) {
        /** @debugFeature forceErrors Logs a 449 response carrying an entry error (|E modifier): url, question, entry identifier and type, and whether it chains onto previous answers. */
        log('449 entry error detected', () => ({
          url: response.url,
          message: response.data.error.message,
          entryForce: response.data.error.entry.force,
          entryType: response.data.error.entry.type,
          chained: Boolean(forceValue),
        }));

        const entryErrorData = {
          previousForce: forceValue,
          message: response.data.error.message,
          entry: response.data.error.entry,
        };
        throw new EntryError(JSON.stringify(entryErrorData));
      }

      /** @debugFeature forceErrors Logs a 449 force response: url, question, offered answers and whether it chains onto previous answers. */
      log('449 force error detected', () => ({
        url: response.url,
        message: response.data.error?.message,
        answers: response.data.error?.force?.answers,
        chained: Boolean(forceValue),
      }));

      const forceErrorData = {
        previousForceHeader: forceValue,
        message: response.data.error.message,
        answers: response.data.error.force.answers,
      };
      throw new ForceError(JSON.stringify(forceErrorData));
    default:
      return response;
  }
};
